import { useEffect, useMemo, useCallback, type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Phone,
  Mail,
  MessageSquare,
  CheckSquare,
  Calendar,
  X,
  PhoneCall,
  FolderOpen,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — JS module, Phase 4 cleanup
import { useDialer } from '@/contexts/DialerContext';
import { hasMeetingBooked } from '@/utils/callUtils';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface TypeMeta {
  bg: string;
  borderColor: string;
  icon: IconType;
  iconClass: string;
  tab: string;
  actionIcon: IconType;
  secondaryIcon: IconType;
  i18nKey: string;
}

interface TypeMetaResolved extends TypeMeta {
  label: string;
  actionLabel: string;
  secondaryLabel: string;
}

interface CallShape {
  id?: string;
  ended_at?: string | Date | null;
  started_at?: string | Date | null;
  created_date?: string | Date | null;
  call_outcome?: string | null;
  direction?: string | null;
  follow_up_at?: string | Date | null;
  follow_up_completed?: boolean;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_phone?: string | null;
}

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  description?: string;
  timestamp?: string | Date | null;
  read: boolean;
  meta?: { phone?: string | null; prospectName?: string | null };
}

interface DialerCtx {
  notifications: NotificationItem[];
  setNotifications: (updater: NotificationItem[] | ((prev: NotificationItem[]) => NotificationItem[])) => void;
  showNotifications: boolean;
  setShowNotifications: (open: boolean) => void;
  allCalls?: CallShape[];
  callerData?: unknown;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// `label`/`actionLabel`/`secondaryLabel` are resolved from the catalog at
// render time (see `useTypeConfig` in the component below), keyed by
// `i18nKey`; colors/icons/tab stay fixed here.
const TYPE_CONFIG: Record<string, TypeMeta> = {
  missed_call: {
    bg: 'bg-red-100',
    borderColor: 'border-l-red-500',
    icon: Phone,
    iconClass: 'text-red-500',
    tab: 'appels',
    actionIcon: PhoneCall,
    secondaryIcon: MessageSquare,
    i18nKey: 'missedCall',
  },
  email: {
    bg: 'bg-blue-100',
    borderColor: 'border-l-blue-500',
    icon: Mail,
    iconClass: 'text-blue-500',
    tab: 'messages',
    actionIcon: Mail,
    secondaryIcon: FolderOpen,
    i18nKey: 'email',
  },
  sms: {
    bg: 'bg-green-100',
    borderColor: 'border-l-green-500',
    icon: MessageSquare,
    iconClass: 'text-green-500',
    tab: 'messages',
    actionIcon: MessageSquare,
    secondaryIcon: FolderOpen,
    i18nKey: 'sms',
  },
  task: {
    bg: 'bg-orange-100',
    borderColor: 'border-l-orange-500',
    icon: CheckSquare,
    iconClass: 'text-orange-500',
    tab: 'taches',
    actionIcon: FolderOpen,
    secondaryIcon: Calendar,
    i18nKey: 'task',
  },
  meeting: {
    bg: 'bg-purple-100',
    borderColor: 'border-l-purple-500',
    icon: Calendar,
    iconClass: 'text-purple-500',
    tab: 'tout',
    actionIcon: FolderOpen,
    secondaryIcon: X,
    i18nKey: 'meeting',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Formats a past ISO date string into a human-readable relative label.
 * e.g. "il y a 3 min", "il y a 2 h", "il y a 4 j"
 */
function formatRelativeTime(isoString: string | Date | null | undefined, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 0) return t('admin:components.notificationBell.relativeTime.justNow');
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return t('admin:components.notificationBell.relativeTime.justNow');
  const mins = Math.floor(secs / 60);
  if (mins < 60) return t('admin:components.notificationBell.relativeTime.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('admin:components.notificationBell.relativeTime.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('admin:components.notificationBell.relativeTime.daysAgo', { count: days });
}

/**
 * Derives an initial set of notifications from the allCalls array.
 * Only generates missed-call and meeting notifications — does not fabricate data.
 */
function deriveNotificationsFromCalls(allCalls: CallShape[], t: (key: string, opts?: Record<string, unknown>) => string): NotificationItem[] {
  const notifications: NotificationItem[] = [];
  const unknown = t('admin:components.notificationBell.unknownContact');

  allCalls.forEach((call) => {
    const id = `derived_${call.id}`;
    const timestamp = call.ended_at || call.started_at || call.created_date;

    // Only inbound calls that weren't answered count as "missed calls"
    // Outbound no_answer/not_reachable are NOT missed calls, they're just unanswered outbound dials
    if (
      (call.call_outcome === 'no_answer' || call.call_outcome === 'not_reachable') &&
      call.direction === 'inbound'
    ) {
      notifications.push({
        id,
        type: 'missed_call',
        title: t('admin:components.notificationBell.missedCallTitle', { name: call.prospect_name || unknown }),
        description: call.prospect_company
          ? `${call.prospect_company} · ${call.prospect_phone || ''}`
          : call.prospect_phone || '',
        timestamp,
        read: false,
        meta: { phone: call.prospect_phone, prospectName: call.prospect_name },
      });
    } else if (hasMeetingBooked(call)) {
      notifications.push({
        id,
        type: 'meeting',
        title: t('admin:components.notificationBell.meetingBookedTitle', { name: call.prospect_name || unknown }),
        description: call.prospect_company
          ? `${call.prospect_company}`
          : t('admin:components.notificationBell.meetingConfirmed'),
        timestamp,
        read: false,
        meta: { phone: call.prospect_phone, prospectName: call.prospect_name },
      });
    } else if (call.call_outcome === 'callback_requested' && call.follow_up_at && !call.follow_up_completed) {
      notifications.push({
        id,
        type: 'task',
        title: t('admin:components.notificationBell.callbackScheduledTitle', { name: call.prospect_name || unknown }),
        description: call.prospect_company || call.prospect_phone || '',
        timestamp,
        read: false,
        meta: { phone: call.prospect_phone, prospectName: call.prospect_name },
      });
    }
  });

  return notifications
    .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
    .slice(0, 50);
}

// ─── Notification Row ─────────────────────────────────────────────────────────

interface NotificationRowProps {
  notification: NotificationItem;
  onAction: (n: NotificationItem) => void;
  onDismiss: (id: string) => void;
}

interface NotificationRowFullProps extends NotificationRowProps {
  typeConfig: Record<string, TypeMetaResolved>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function NotificationRow({ notification, onAction, onDismiss, typeConfig, t }: NotificationRowFullProps) {
  const config = typeConfig[notification.type] || typeConfig.task!;
  const Icon = config.icon;
  const ActionIcon = config.actionIcon;
  const SecondaryIcon = config.secondaryIcon;

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-accent/50 border-l-[3px] ${config.borderColor} ${
        notification.read ? 'opacity-60' : ''
      }`}
    >
      {/* Icon badge */}
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center mt-0.5`}
      >
        <Icon className={`w-3.5 h-3.5 ${config.iconClass}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className={`text-[9px] font-bold tracking-wider ${config.iconClass}`}>
              {config.label}
            </span>
            <p className="text-sm font-medium text-foreground truncate leading-snug">
              {notification.title}
            </p>
          </div>
          <button
            onClick={() => onDismiss(notification.id)}
            className="flex-shrink-0 text-muted-foreground/40 hover:text-muted-foreground transition-colors mt-0.5"
            aria-label={t('admin:components.notificationBell.dismissAria')}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {notification.description && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{notification.description}</p>
        )}
        <div className="flex items-center justify-between mt-2 gap-2">
          <span className="text-[10px] text-muted-foreground/60">
            {formatRelativeTime(notification.timestamp, t)}
          </span>
          {/* Dual action buttons — PDF spec: 2 buttons per notification with hover fill */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onAction(notification)}
              className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-border text-foreground/80 hover:bg-primary hover:text-white hover:border-primary transition-all"
            >
              <ActionIcon className="w-3 h-3" />
              {config.actionLabel}
            </button>
            {config.secondaryLabel && (
              <button
                onClick={() => onDismiss(notification.id)}
                className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-border text-muted-foreground hover:bg-primary hover:text-white hover:border-primary transition-all"
              >
                <SecondaryIcon className="w-3 h-3" />
                {config.secondaryLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function NotificationBell() {
  const { t } = useTranslation(['admin']);
  const { notifications, setNotifications, showNotifications, setShowNotifications, allCalls } =
    useDialer() as DialerCtx;

  const typeConfig = useMemo<Record<string, TypeMetaResolved>>(() => {
    const out: Record<string, TypeMetaResolved> = {};
    for (const [key, meta] of Object.entries(TYPE_CONFIG)) {
      out[key] = {
        ...meta,
        label: t(`admin:components.notificationBell.types.${meta.i18nKey}.label`),
        actionLabel: t(`admin:components.notificationBell.types.${meta.i18nKey}.action`),
        secondaryLabel: t(`admin:components.notificationBell.types.${meta.i18nKey}.secondary`),
      };
    }
    return out;
  }, [t]);

  // Seed from allCalls on first mount / when allCalls changes, but only for entries
  // not already tracked (avoid duplicates on re-renders).
  useEffect(() => {
    if (!allCalls?.length) return;

    const derived = deriveNotificationsFromCalls(allCalls, t);
    setNotifications((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const fresh = derived.filter((n) => !existingIds.has(n.id));
      if (!fresh.length) return prev;
      return [...fresh, ...prev]
        .sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
        .slice(0, 50);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCalls]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const markAllRead = useCallback(() => {
    // Clear all notifications (user expects them to disappear, not just go grey)
    setNotifications([]);
  }, [setNotifications]);

  const dismissNotification = useCallback(
    (id: string) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [setNotifications]
  );

  const handleAction = useCallback(
    (notification: NotificationItem) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );

      // For missed calls, dispatch a custom event that the dialer can intercept
      // to open the prospect detail panel or initiate a call.
      if (notification.type === 'missed_call' && notification.meta?.phone) {
        window.dispatchEvent(
          new CustomEvent('xdial:recallProspect', {
            detail: { phone: notification.meta.phone, name: notification.meta.prospectName },
          })
        );
      }
    },
    [setNotifications]
  );

  // ─── Tab filtering ────────────────────────────────────────────────────────

  const byTab = useCallback(
    (tab: string) => {
      if (tab === 'tout') return notifications;
      return notifications.filter((n) => {
        const cfg = TYPE_CONFIG[n.type];
        if (!cfg) return false;
        if (tab === 'appels') return cfg.tab === 'appels';
        if (tab === 'messages') return cfg.tab === 'messages';
        if (tab === 'taches') return cfg.tab === 'taches';
        return false;
      });
    },
    [notifications]
  );

  // ─── Empty state ──────────────────────────────────────────────────────────

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <Bell className="w-8 h-8 text-muted-foreground/40 mb-2" />
      <p className="text-sm text-muted-foreground/60">{t('admin:components.notificationBell.empty')}</p>
    </div>
  );

  // ─── Notification list for a given tab ───────────────────────────────────

  const NotificationList = ({ items }: { items: NotificationItem[] }) => {
    if (!items.length) return <EmptyState />;
    return (
      <div className="divide-y divide-border/50">
        {items.map((n) => (
          <NotificationRow
            key={n.id}
            notification={n}
            onAction={handleAction}
            onDismiss={dismissNotification}
            typeConfig={typeConfig}
            t={t}
          />
        ))}
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Popover open={showNotifications} onOpenChange={setShowNotifications}>
      <PopoverTrigger asChild>
        <button
          className="relative flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          aria-label={`${t('admin:components.notificationBell.title')}${unreadCount > 0 ? t('admin:components.notificationBell.unreadSuffix', { count: unreadCount }) : ''}`}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="p-0 w-[380px] max-h-96 overflow-hidden flex flex-col shadow-xl border border-border rounded-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">{t('admin:components.notificationBell.title')}</span>
            {unreadCount > 0 && (
              <Badge
                variant="secondary"
                className="bg-red-100 text-red-600 text-[10px] px-1.5 py-0 h-4 font-semibold"
              >
                {unreadCount}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
            >
              {t('admin:components.notificationBell.markAllRead')}
            </button>
          )}
        </div>

        {/* Tabs + content */}
        <Tabs defaultValue="tout" className="flex flex-col flex-1 min-h-0">
          <TabsList className="flex-shrink-0 w-full rounded-none border-b border-border/50 bg-card h-auto p-0 justify-start gap-0">
            {[
              { value: 'tout', label: t('admin:components.notificationBell.tabs.all') },
              { value: 'appels', label: t('admin:components.notificationBell.tabs.calls') },
              { value: 'messages', label: t('admin:components.notificationBell.tabs.messages') },
              { value: 'taches', label: t('admin:components.notificationBell.tabs.tasks') },
            ].map(({ value, label }) => {
              const count = byTab(value).filter((n) => !n.read).length;
              return (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="relative flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-500 data-[state=active]:text-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors"
                >
                  {label}
                  {count > 0 && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold">
                      {count > 9 ? '9+' : count}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto">
            <TabsContent value="tout" className="mt-0">
              <NotificationList items={byTab('tout')} />
            </TabsContent>
            <TabsContent value="appels" className="mt-0">
              <NotificationList items={byTab('appels')} />
            </TabsContent>
            <TabsContent value="messages" className="mt-0">
              <NotificationList items={byTab('messages')} />
            </TabsContent>
            <TabsContent value="taches" className="mt-0">
              <NotificationList items={byTab('taches')} />
            </TabsContent>
          </div>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
